import React, { useState } from 'react';
import { X, Send, MessageCircle } from 'lucide-react';
import { Job } from '../types/Job';

interface JobApplicationModalProps {
  job: Job;
  isOpen: boolean;
  onClose: () => void;
}

const JobApplicationModal: React.FC<JobApplicationModalProps> = ({ job, isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    consent: false,
    honeypot: '' // Hidden field for spam protection
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    // Honeypot check
    if (formData.honeypot) {
      setSubmitStatus({
        type: 'success',
        message: '¡Gracias! Hemos recibido tu solicitud y te contactaremos pronto.'
      });
      setTimeout(() => {
        onClose();
        resetForm();
      }, 2000);
      setSubmitting(false);
      return;
    }

    // Validate consent
    if (!formData.consent) {
      setSubmitStatus({
        type: 'error',
        message: 'Debes aceptar que te contactemos para continuar.'
      });
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/.netlify/functions/send-job-application', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          jobId: job.id,
          jobTitle: job.title,
          company: job.company
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: '¡Solicitud enviada! Hemos recibido tu solicitud para ' + job.title + '. Te contactaremos pronto a través de ' + formData.email
        });
        // Close modal and reset after 3 seconds
        setTimeout(() => {
          onClose();
          resetForm();
        }, 3000);
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.error || 'Error al enviar la solicitud. Por favor, intenta de nuevo.'
        });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Error de conexión. Por favor, verifica tu conexión a internet e intenta de nuevo.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      consent: false,
      honeypot: ''
    });
    setSubmitStatus({ type: null, message: '' });
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      `Hola, estoy interesado/a en la oferta de ${job.title} en ${job.company} (${job.location}, ${job.country}). Me gustaría recibir más información. ¡Gracias!`
    );
    const whatsappUrl = `https://wa.me/34678601100?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Solicitar Empleo</h3>
            <p className="text-gray-600">
              <strong>{job.title}</strong> en {job.company}
            </p>
            <p className="text-sm text-gray-500">{job.location}, {job.country}</p>
          </div>

          {/* Status Messages */}
          {submitStatus.type && (
            <div className={`mb-4 p-4 rounded-xl ${
              submitStatus.type === 'success'
                ? 'bg-green-50 border-2 border-green-200 text-green-800'
                : 'bg-red-50 border-2 border-red-200 text-red-800'
            }`}>
              {submitStatus.message}
            </div>
          )}

          {/* Application Form */}
          <form onSubmit={handleSubmit} className="space-y-4 mb-4">
            <div>
              <label htmlFor="modal-name" className="block text-sm font-medium text-gray-700 mb-1">
                Nombre Completo *
              </label>
              <input
                type="text"
                id="modal-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={submitting}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-accent focus:border-accent transition-all disabled:bg-gray-100"
                placeholder="Tu nombre completo"
                maxLength={100}
              />
            </div>

            <div>
              <label htmlFor="modal-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                id="modal-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={submitting}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-accent focus:border-accent transition-all disabled:bg-gray-100"
                placeholder="tu@email.com"
                maxLength={255}
              />
            </div>

            <div>
              <label htmlFor="modal-phone" className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono *
              </label>
              <input
                type="tel"
                id="modal-phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                disabled={submitting}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-accent focus:border-accent transition-all disabled:bg-gray-100"
                placeholder="+34 123 456 789"
                maxLength={20}
              />
            </div>

            {/* Honeypot field - hidden from users */}
            <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
              <label htmlFor="modal-honeypot">Leave this empty</label>
              <input
                type="text"
                id="modal-honeypot"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* Consent checkbox */}
            <div className="flex items-start space-x-3 bg-blue-50 p-4 rounded-xl">
              <input
                type="checkbox"
                id="modal-consent"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                required
                disabled={submitting}
                className="mt-1 w-4 h-4 text-accent focus:ring-accent border-gray-300 rounded"
              />
              <label htmlFor="modal-consent" className="text-sm text-gray-700">
                Acepto que TrabajoFuera me contacte sobre esta oportunidad laboral y otras ofertas similares.
                Puedo retirar mi consentimiento en cualquier momento.
              </label>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={submitting || !formData.consent}
              className="w-full bg-accent hover:bg-accent-dark text-white px-6 py-3 rounded-full font-bold hover:scale-105 transition-all shadow-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <Send className="w-5 h-5" />
              <span>{submitting ? 'Enviando...' : 'Enviar Solicitud'}</span>
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">o contacta por</span>
            </div>
          </div>

          {/* WhatsApp button */}
          <button
            type="button"
            onClick={handleWhatsAppClick}
            className="w-full bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-full font-bold hover:scale-105 transition-all shadow-lg flex items-center justify-center space-x-2"
          >
            <MessageCircle className="w-5 h-5" />
            <span>Contactar por WhatsApp</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobApplicationModal;
